// [S-263 → merged into Upload Portal] "All uploads" — the durable, tenant-scoped
// status board for every document the studio has ingested, across every
// lifecycle state (received → … → in review → searchable → failed).
//
// This is the operational tail of the Upload action: the live per-file queue on
// the Upload tab is client-side session state and starts empty on refresh; this
// panel queries GET /admin/documents so it survives reloads and shows the
// studio's whole corpus-in-progress. It intentionally lives UNDER the Upload
// Portal (as a tab) rather than as a peer of Precedent — Precedent is the
// readable, finished library (indexed only); this is where-are-my-uploads.
//
// Rows are readable now: filename + auto-detected type/guild/tier, not the raw
// document hash. (Party names / amounts only exist post-index in the
// StructuredContract, so the searchable, richer cards live on Precedent.)
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Button, Pagination, Select, usePagination } from "../components";
import { useSession } from "../auth/AuthProvider";
import { sessionCan } from "../auth/capabilities";
import { ReviewQueueBanner } from "./ReviewQueueBanner";
import {
  DOCUMENT_LIFECYCLE_STATES,
  useIngestionDocuments,
  useReplayDocument,
  useReplayDocuments,
  type DocumentLifecycleState,
  type IngestionDocumentRow,
} from "../data/ingestionQuery";

const STATE_OPTIONS: ReadonlyArray<{
  value: DocumentLifecycleState;
  label: string;
}> = DOCUMENT_LIFECYCLE_STATES.map((s) => ({ value: s, label: prettyState(s) }));

export function AllUploadsPanel({
  // Explicit tenant scope for platform_admin (who has no studio of their own and
  // picks one above). Tenant users leave this undefined and the server forces
  // their own studio scope.
  scopeTenantId,
  needsPick = false,
}: {
  scopeTenantId?: string;
  needsPick?: boolean;
}): JSX.Element {
  const session = useSession();
  // Review-queue actions gate on review:moderate, not a role name.
  const canReview = sessionCan(session, "review:moderate");
  // [S-671] Self-service recovery for stuck/failed uploads gates on
  // ingestion:operate — tenant_admin of their own tenant, or platform_admin.
  const canRetry = sessionCan(session, "ingestion:operate");
  const replay = useReplayDocument();
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const retryDocument = (documentId: string): void => {
    setRetryingId(documentId);
    replay.mutate(
      { documentId, fromStage: "1" },
      { onSettled: () => setRetryingId(null) }
    );
  };
  // [bulk-retry] One action for the whole failed set in view.
  const retryAll = useReplayDocuments();
  const [retryProgress, setRetryProgress] = useState(0);
  const [stateFilter, setStateFilter] = useState<
    DocumentLifecycleState | undefined
  >(undefined);
  const [mineOnly, setMineOnly] = useState(false);

  // Fetch the whole set ONCE (no `state` param) and filter client-side. Sending
  // the state filter to the server narrows the response, which used to zero out
  // every other bucket's count (the counts are derived from what came back). It
  // also avoids a refetch/loading flash on every bucket click.
  const query = useIngestionDocuments({
    ...(scopeTenantId !== undefined ? { tenantId: scopeTenantId } : {}),
    limit: 500,
  });

  const allDocs = query.data ?? [];

  // "My uploads" scope applies to BOTH the counts and the rows.
  const scopedDocs = useMemo(
    () =>
      mineOnly && session?.uid !== undefined
        ? allDocs.filter((d) => d.uploadedBy === session.uid)
        : allDocs,
    [allDocs, mineOnly, session?.uid]
  );

  // Counts are over the full (mine-scoped) set, independent of the active state
  // filter — each bucket is a filter target, so it must show its own true total.
  const counts = useMemo(() => {
    const buckets: Record<DocumentLifecycleState, number> = {
      received: 0,
      parsing: 0,
      stage1_complete: 0,
      stage2_review: 0,
      stage3_human: 0,
      indexed: 0,
      failed: 0,
    };
    for (const d of scopedDocs) buckets[d.lifecycleState] += 1;
    return buckets;
  }, [scopedDocs]);

  // Rows apply the state filter on top of the mine scope (client-side).
  const docs = useMemo(
    () =>
      stateFilter === undefined
        ? scopedDocs
        : scopedDocs.filter((d) => d.lifecycleState === stateFilter),
    [scopedDocs, stateFilter]
  );
  const pager = usePagination(docs, 25);

  // [bulk-retry] Every failed document in the CURRENT scope — not just the
  // visible page, since the whole point is to clear a batch of ~100 without
  // paging through it. `scopedDocs` (not `docs`) so it still means "all failed
  // in this scope" when the user has filtered to another state.
  const failedDocs = useMemo(
    () => scopedDocs.filter((d) => d.lifecycleState === "failed"),
    [scopedDocs]
  );
  const retryAllFailed = (): void => {
    setRetryProgress(0);
    retryAll.mutate({
      documentIds: failedDocs.map((d) => d.id),
      fromStage: "1",
      onProgress: (settled) => setRetryProgress(settled),
    });
  };

  // platform_admin who hasn't picked an organization yet — nothing to scope to.
  if (needsPick) {
    return (
      <div className="ingest__state" role="status" data-testid="archive-pick">
        Pick an organization above to see its uploads.
      </div>
    );
  }

  return (
    <>
      <div className="arch__intro">
        Every document your studio has uploaded, newest first — including the
        ones still working through review.{" "}
        {query.isLoading
          ? "Loading…"
          : `${docs.length} document${docs.length === 1 ? "" : "s"} shown.`}
      </div>

      {canReview && <ReviewQueueBanner />}

      <div className="ingest__counts">
        {DOCUMENT_LIFECYCLE_STATES.map((s) => (
          <Button
            key={s}
            variant="bare"
            className={`ingest__bucket ingest__bucket--${s}${
              stateFilter === s ? " is-on" : ""
            }`}
            onClick={() =>
              setStateFilter((prev) => (prev === s ? undefined : s))
            }
            data-testid={`archive-bucket-${s}`}
          >
            <span className="ingest__bucket__num">{counts[s]}</span>
            <span className="ingest__bucket__lbl">{prettyState(s)}</span>
          </Button>
        ))}
      </div>

      <div className="ingest__filter">
        <span className="ingest__filter__lbl">State</span>
        <Select
          label="Lifecycle state"
          value={stateFilter}
          options={STATE_OPTIONS}
          onValueChange={(v) => setStateFilter(v)}
          triggerClassName="ingest__filter__select"
        />
        {stateFilter !== undefined && (
          <Button
            variant="ghost"
            className="btn--xs"
            onClick={() => setStateFilter(undefined)}
          >
            Clear
          </Button>
        )}
        <Button
          variant={mineOnly ? "secondary" : "ghost"}
          className="btn--xs"
          onClick={() => setMineOnly((v) => !v)}
          data-testid="archive-mine-toggle"
          aria-pressed={mineOnly}
        >
          {mineOnly ? "✓ My uploads" : "My uploads"}
        </Button>
        {/* [bulk-retry] Recovering a bad batch one row at a time isn't a
            workflow — the 2026-08-15 stage-1 OOM left 109 documents needing it.
            Retries every failed document in the CURRENT view (so "My uploads"
            and any filter scope it), sequentially: the pipeline is capped at 4
            concurrent parses and a burst of replays is what broke it before. */}
        {canRetry && failedDocs.length > 1 && (
          <Button
            variant="secondary"
            className="btn--xs"
            onClick={retryAllFailed}
            loading={retryAll.isPending}
            data-testid="archive-retry-all"
          >
            {retryAll.isPending
              ? `Retrying ${retryProgress}/${failedDocs.length}…`
              : `Retry ${failedDocs.length} failed`}
          </Button>
        )}
      </div>

      {retryAll.data !== undefined && !retryAll.isPending && (
        // Alert has no "success" tone — an all-clear result renders untoned,
        // and only a partial failure earns the warning styling.
        <Alert
          label={`Retried ${retryAll.data.ok.length} document${retryAll.data.ok.length === 1 ? "" : "s"}`}
          {...(retryAll.data.failed.length > 0
            ? ({ tone: "warning" } as const)
            : {})}
        >
          {retryAll.data.failed.length > 0
            ? `${retryAll.data.failed.length} couldn't be re-queued (${retryAll.data.failed[0]?.message ?? "unknown"}). The rest are back in the pipeline.`
            : "They're back in the pipeline — watch the states above."}
        </Alert>
      )}

      {(query.isLoading || (query.isFetching && docs.length === 0)) && (
        <div className="ingest__state" role="status">
          Loading documents…
        </div>
      )}
      {query.isError && (
        <Alert label="Couldn't load" tone="error">
          {query.error?.message ?? "unknown error"}
        </Alert>
      )}
      {!query.isLoading &&
        !query.isFetching &&
        !query.isError &&
        docs.length === 0 && (
          <div className="ingest__state" data-testid="archive-empty">
            {mineOnly
              ? "You haven't uploaded any documents yet."
              : stateFilter === undefined
                ? "No uploads yet. Drop files on the Upload tab and they'll appear here."
                : `No documents in state '${prettyState(stateFilter)}'.`}
          </div>
        )}

      {!query.isLoading && !query.isError && docs.length > 0 && (
        <>
          <ArchiveTable
            rows={pager.pageItems}
            canRetry={canRetry}
            retryingId={retryingId}
            onRetry={retryDocument}
          />
          <Pagination
            page={pager.page}
            pageCount={pager.pageCount}
            onPageChange={pager.setPage}
            total={pager.total}
            rangeStart={pager.rangeStart}
            rangeEnd={pager.rangeEnd}
            itemLabel="document"
          />
        </>
      )}
    </>
  );
}

function ArchiveTable({
  rows,
  canRetry,
  retryingId,
  onRetry,
}: {
  rows: IngestionDocumentRow[];
  canRetry: boolean;
  retryingId: string | null;
  onRetry: (documentId: string) => void;
}): JSX.Element {
  return (
    <div className="arch__tbl" role="table" aria-label="All uploads">
      <div className="arch__row arch__row--head" role="row">
        <div role="columnheader" className="arch__cell">
          Document
        </div>
        <div role="columnheader" className="arch__cell">
          State
        </div>
        <div role="columnheader" className="arch__cell">
          Uploaded
        </div>
        <div role="columnheader" className="arch__cell arch__cell--act">
          Actions
        </div>
      </div>
      {rows.map((d) => (
        <div
          className="arch__row"
          key={d.id}
          role="row"
          data-testid={`archive-row-${d.id}`}
        >
          <div role="cell" className="arch__cell arch__cell--doc">
            <div
              className={
                d.filename != null ? "arch__name" : "arch__name arch__name--untitled"
              }
              title={d.filename ?? "Uploaded before filenames were captured"}
            >
              {d.filename ?? "Untitled upload"}
            </div>
            <div className="arch__tags">
              {d.dealType != null && (
                <span className="arch__tag">{prettyTag(d.dealType)}</span>
              )}
              {d.guild != null && <span className="arch__tag">{d.guild}</span>}
              {d.talentTier != null && (
                <span className="arch__tag">{prettyTag(d.talentTier)}</span>
              )}
              {d.filename == null &&
                d.dealType == null &&
                d.guild == null &&
                d.talentTier == null && (
                  <span className="arch__tag arch__tag--muted">
                    Classifying…
                  </span>
                )}
            </div>
          </div>
          <div role="cell" className="arch__cell">
            <StatePill state={d.lifecycleState} />
          </div>
          <div role="cell" className="arch__cell">
            {formatTs(d.uploadedAt)}
          </div>
          <div role="cell" className="arch__cell arch__cell--act">
            {d.lifecycleState === "stage3_human" && (
              <Link to="/queue" className="btn btn--s">
                Review →
              </Link>
            )}
            {d.lifecycleState === "indexed" && (
              <Link to="/precedent" className="btn btn--s btn--g">
                Search →
              </Link>
            )}
            {/* [S-671] Failed docs previously had no recourse — dedup on
                documentHash silently blocked re-upload, and replay was
                platform_admin-only. Self-service retry, capability-gated. */}
            {d.lifecycleState === "failed" && canRetry && (
              <Button
                variant="ghost"
                className="btn--s"
                onClick={() => onRetry(d.id)}
                loading={retryingId === d.id}
                data-testid={`archive-retry-${d.id}`}
              >
                Retry →
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function StatePill({ state }: { state: DocumentLifecycleState }): JSX.Element {
  return (
    <span className={`ingest__pill ingest__pill--${state}`}>
      {prettyState(state)}
    </span>
  );
}

function prettyState(s: DocumentLifecycleState): string {
  switch (s) {
    case "received":
      return "Received";
    case "parsing":
      return "Parsing";
    case "stage1_complete":
      return "Stage 1 done";
    case "stage2_review":
      return "Stage 2 review";
    case "stage3_human":
      return "In review";
    case "indexed":
      return "Searchable";
    case "failed":
      return "Failed";
  }
}

function prettyTag(v: string): string {
  return v.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function formatTs(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
